<template>
    <v-dialog v-model="modal" max-width="750" scrollable :retain-focus="false">
        <v-card>
            <v-card-title>
                <div class="py-1 ps-3 d-flex full-width align-center">
                    <span>{{ $t('send_email') }}</span>
                    <v-spacer />
                    <v-btn icon="mdi-close" variant="text" @click="modal = false" />
                </div>
            </v-card-title>
            <v-card-text>
                <v-form v-model="valid" :disabled="loading">
                    <v-checkbox
                        v-if="isTO1"
                        v-model="emailFor"
                        value="suppliers"
                        :label="
                            $t(
                                'suppliers',
                                !specificService && order && order.services && order.services.length > 1 ? 2 : 1
                            )
                        "
                        hide-details
                    />
                    <v-checkbox
                        v-if="isTO1 || isTO2"
                        v-model="emailFor"
                        value="clients"
                        :label="$t('client')"
                        hide-details
                    />
                    <v-checkbox
                        v-if="isTO1 || isTO2"
                        v-model="emailFor"
                        value="travelers"
                        :label="$t('travelers')"
                        hide-details
                        class="mb-6"
                    />
                    <div v-if="specificService" class="d-flex align-md-center flex-column flex-md-row align-end">
                        <v-select
                            v-model="templateId"
                            :items="templateItems"
                            :label="$t('template')"
                            :loading="templatesLoading"
                            :disabled="!templates.length || templatesLoading || !emailFor"
                            clearable
                            @update:model-value="selectTemplate"
                        />
                        <div
                            v-show="
                                selectedTemplate &&
                                selectedTemplate.templateLocales &&
                                selectedTemplate.templateLocales.length
                            "
                            class="mt-n6 ms-1"
                        >
                            <locale-switcher
                                :locale-items="(selectedTemplate && selectedTemplate.templateLocales) || []"
                                @change="onLocaleChange"
                            />
                        </div>
                    </div>
                    <v-text-field
                        v-model="message.subject"
                        :label="`${$t('email_form.subject')}*`"
                        :rules="[requiredRule('email_form.subject')]"
                        clearable
                    />
                    <v-text-field
                        v-model="message.from"
                        :label="`${$t('email_form.from')}*`"
                        :rules="validationRules('email_form.from', [emailRule, requiredRule])"
                        required
                    />
                    <emails-field
                        v-model="message.to"
                        :label="`${$t('email_form.to')}${toFieldRequired ? '*' : ''}`"
                        :required="toFieldRequired"
                    />
                    <emails-field v-model="message.cc" :label="$t('email_form.cc')" />
                    <emails-field
                        v-model="message.bcc"
                        :label="`${$t('email_form.bcc')}${!toFieldRequired ? '*' : ''}`"
                        :required="!toFieldRequired"
                    />
                    <v-input
                        v-model="emailHtml"
                        :rules="[...(!templateId ? [requiredRule('email_form.text')] : [])]"
                        class="mb-7"
                    >
                        <NewHtmlEditor v-model="emailHtml" :label="`${$t('email_form.text')}*`" class="full-width" />
                    </v-input>
                    <v-expansion-panels
                        v-if="services.length || invoices.length || documents.length || customDocuments.length"
                        class="mb-5"
                    >
                        <v-expansion-panel>
                            <v-expansion-panel-title>
                                {{ $t('documents') }}
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <v-expansion-panels>
                                    <v-expansion-panel v-if="services.length && isShowVoucherBtn">
                                        <v-expansion-panel-title>
                                            <v-checkbox
                                                :model-value="allSelected(services)"
                                                :label="`${$t('voucher')} (${services.length})`"
                                                hide-details
                                                density="compact"
                                                @click.stop="toggleAllSelected(services)"
                                            />
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <v-list>
                                                <v-list-item v-for="service in services" :key="service.processId">
                                                    <v-checkbox
                                                        v-model="service.selected"
                                                        :label="service.serviceName"
                                                        hide-details
                                                        density="compact"
                                                    />
                                                </v-list-item>
                                            </v-list>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                    <v-expansion-panel v-if="invoices.length">
                                        <v-expansion-panel-title>
                                            <v-checkbox
                                                :model-value="allSelected(invoices)"
                                                :label="`${$t('invoices.invoice')} (${invoices.length})`"
                                                hide-details
                                                density="compact"
                                                @click.stop="toggleAllSelected(invoices)"
                                            />
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <v-list>
                                                <v-list-item v-for="invoice in invoices" :key="invoice.invoiceId">
                                                    <v-checkbox
                                                        v-model="invoice.selected"
                                                        :label="invoice.invoiceName"
                                                        hide-details
                                                        density="compact"
                                                    />
                                                </v-list-item>
                                            </v-list>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                    <v-expansion-panel v-if="documents.length">
                                        <v-expansion-panel-title>
                                            <v-checkbox
                                                :model-value="allSelected(documents)"
                                                :label="`${$t('documents')} (${documents.length})`"
                                                hide-details
                                                density="compact"
                                                @click.stop="toggleAllSelected(documents)"
                                            />
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <v-list>
                                                <v-list-item v-for="document in documents" :key="document.id">
                                                    <v-checkbox
                                                        v-model="document.selected"
                                                        :label="document.name"
                                                        hide-details
                                                        density="compact"
                                                    />
                                                </v-list-item>
                                            </v-list>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                                <v-list v-if="customDocuments.length" class="ml-2">
                                    <v-list-item v-for="(document, index) in customDocuments" :key="index">
                                        <v-checkbox
                                            v-model="document.selected"
                                            :label="document.name"
                                            hide-details
                                            density="compact"
                                        />
                                    </v-list-item>
                                </v-list>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                    <v-file-input
                        v-model="attachments"
                        :clearable="true"
                        :label="$t('email_form.attachments')"
                        multiple
                        chips
                        show-size
                        class="cursor-pointer"
                        @change="onAddFiles($event.target.files)"
                        @click:clear="onClearFiles"
                    >
                        <template v-slot:selection>
                            {{ $t('email_form.attachments') }} ({{ attachments.length }})
                        </template>
                    </v-file-input>
                    <div>
                        <v-file-upload-item
                            v-for="(item, index) in attachments"
                            :key="item.name"
                            clearable
                            lines="one"
                            nav
                        >
                            {{ item.name }}
                            <template v-slot:clear>
                                <v-btn color="grey-darken-1" @click="removeFile(index)" />
                            </template>
                        </v-file-upload-item>
                    </div>
                </v-form>
            </v-card-text>
            <v-card-actions class="justify-center">
                <template v-if="templateId">
                    <!--<v-btn-->
                    <!--    variant="flat"-->
                    <!--    size="large"-->
                    <!--    :disabled="!valid || loading || !selectedTemplate"-->
                    <!--    @click="edit = true"-->
                    <!--&gt;-->
                    <!--    {{ $t('edit') }}-->
                    <!--</v-btn>-->
                    <v-btn
                        variant="flat"
                        size="large"
                        color="secondary"
                        :disabled="!valid || loading || !selectedTemplate"
                        @click="preview = true"
                    >
                        {{ $t('preview') }}
                    </v-btn>
                </template>
                <v-btn
                    variant="flat"
                    size="large"
                    color="primary"
                    :disabled="!valid || loading || (templateId && !selectedTemplate)"
                    :loading="loading"
                    @click="send"
                >
                    {{ $t('send') }}
                </v-btn>
            </v-card-actions>
        </v-card>
        <email-preview-modal v-model="preview" :html="emailHtml" />
        <!--<email-edit-modal v-model="edit" :json="emailJson" @change="editedTemplateHtml = $event" />-->
    </v-dialog>
</template>

<script lang="ts">
    import {Component, mixins, Watch} from 'vue-facing-decorator'
    import {EventBus, SHOW_SEND_EMAIL_MODAL} from '@/utils/event-bus'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import EmailsField from '@/components/snippets/forms/EmailsField.vue'
    import {blobToBase64} from '@/utils/helpers'
    import {VoucherMixin} from '@/components/account/mixins/VoucherMixin.vue'
    import EmailPreviewModal from '@/components/account/modals/EmailPreviewModal.vue'
    import EmailEditModal from '@/components/account/modals/EmailEditModal.vue'
    import {OrderEmailsMixin} from '@/components/account/mixins/OrderEmailsMixin.vue'
    import {customDocumentFileName} from '@/utils/document-utils'
    import {wrapHtmlMail} from '@/utils/email'

    import Handlebars from 'handlebars'
    import SkeletonLoader from '~/components/SkeletonLoader.vue'

    const message = () => ({
        subject: '',
        from: '',
        to: [],
        cc: [],
        bcc: [],
        html: '',
        attachments: [],
    })

    @Component({
        components: {
            EmailEditModal,
            EmailPreviewModal,
            EmailsField,
            NewHtmlEditor: defineAsyncComponent({
                loader: () => import('@/components/admin/NewHtmlEditor.vue'),
                loadingComponent: SkeletonLoader,
            }),
        },
    })
    export class SendEmailModal extends mixins(FormValidationRulesMixin, VoucherMixin, OrderEmailsMixin) {
        modal = false
        valid = false
        loading = false
        message = message()
        attachments = []
        attachmentsPrevious = []

        orderId = null
        directSales = true
        templateId = null
        templateData = null
        order = null
        checkboxServicesSelected = null
        services = []
        service = null
        invoices = []
        documents = []
        customDocuments = []

        templates = []
        preview = false
        edit = false
        editedTemplateHtml = null
        toFieldRequired = true
        specificService = false
        templatesLoading = false
        selectedTemplate = null
        emailHtml = ''

        onAddFiles(files) {
            this.attachmentsPrevious = [...this.attachmentsPrevious, ...files]
            this.attachments = this.attachmentsPrevious
        }

        removeFile(index) {
            this.attachments.splice(index, 1)
            this.attachmentsPrevious = this.attachments
        }

        onClearFiles() {
            this.attachments = []
            this.attachmentsPrevious = []
        }

        @Watch('emailFor.length')
        async setEmails() {
            const emails = await this.getOrderEmails(
                this.services.length === 1 ? [{...this.order, services: this.services}] : [this.order]
            )
            if (emails.length === 1) {
                this.message.to = emails
                this.message.bcc = []
                //this.toFieldRequired = true
            } else {
                this.message.to = []
                this.message.bcc = emails
                //this.toFieldRequired = false
            }
        }

        @Watch('message.to.length')
        checkIsToFieldRequired() {
            if (this.message.to.length === 0 && this.message.bcc.length > 0) {
                this.toFieldRequired = false
            } else {
                this.toFieldRequired = true
            }
        }

        async mounted() {
            EventBus.$on(SHOW_SEND_EMAIL_MODAL, this.showModal)
        }

        beforeUnmount() {
            EventBus.$off(SHOW_SEND_EMAIL_MODAL, this.showModal)
        }

        async selectTemplate(id, localeCode = null) {
            if (id && this.selectedTemplate?.id !== id) {
                try {
                    this.templatesLoading = true
                    const {
                        templates: [selectedTemplate],
                    } = await this.$api.notificationTemplates.get({id, ...(localeCode && {localeCode})})

                    this.selectedTemplate = selectedTemplate
                    this.message.subject = this.emailSubject

                    await this.loadHtmlPreview(id, localeCode)
                } catch (e) {
                    this.selectedTemplate = null
                } finally {
                    this.templatesLoading = false
                }
            } else {
                this.selectedTemplate = null
            }
        }

        async onLocaleChange(localeCode) {
            const id = this.selectedTemplate?.id
            this.selectedTemplate = null
            this.emailHtml = ''
            await this.selectTemplate(id, localeCode)
        }

        async loadHtmlPreview(id, localeCode = null) {
            try {
                this.emailHtml = await this.$api.emailNotificationTemplatePreview.get({
                    processId: this.service.processId,
                    templateId: id,
                    htmlOnly: true,
                    ...(localeCode && {localeCode}),
                })
            } catch (e) {
                this.emailHtml = ''
            }
        }

        async showModal({order, service, templates: documentTemplates, checkboxServicesSelected}) {
            this.order = order
            this.checkboxServicesSelected = checkboxServicesSelected
            const {orderId, objectId, services, clientType, clientPerson, customerPaymentDate, orderPrices} = order
            this.modal = true
            this.orderId = orderId
            this.service = service || this.getSortedServices(this.order.services)[0]
            if (this.$conf.public.nodemailerMailFrom) {
                this.message.from = this.$conf.public.nodemailerMailFrom
            }
            const docsList = async fn => {
                try {
                    return (await fn()).map(item => ({selected: false, ...item}))
                } catch (e) {
                    return []
                }
            }

            if (!this.templates.length && service) {
                try {
                    this.templatesLoading = true
                    const {templates} = await this.$api.notificationTemplates.get({skipTemplate: true})
                    this.templates = templates
                        // TODO [P0]: it's don't need already???
                        // .filter(({notificationType}) =>
                        //     ['PAYMENT_RECEIPT', 'DEFAULT_SIGNATURE'].includes(notificationType)
                        // )
                        .map(({id, notificationType, recipientType, htmlTemplate, jsonTemplate, info}) => ({
                            value: id,
                            title: info || this.$t(`admin.notificationType.${notificationType}`),
                            notificationType,
                            recipientType,
                            htmlTemplate,
                            jsonTemplate,
                        }))
                } catch (e) {
                    this.templates = []
                } finally {
                    this.templatesLoading = false
                }
            }

            if (!service) {
                this.specificService = false
                this.services = this.getSortedServices(services)
                    .filter(service => runtimeStore.isServiceVoucherAvailable(service))
                    .map(item => ({selected: this.allServicesPreselected, ...item}))
                const loadInvoices = async () => {
                    this.invoices = await docsList(
                        async () =>
                            (
                                await this.$api.invoices.get({
                                    orderId: objectId,
                                    invoiceType: 'CUSTOMER',
                                    active: true,
                                })
                            ).invoices
                    )
                }
                this.initCustomDocuments(documentTemplates)
                const loadDocuments = async () => {
                    this.documents = await docsList(async () => await this.$api.documents.get({orderId}))
                }
                await Promise.all([loadInvoices(), loadDocuments()])
            } else {
                this.specificService = true
                this.services = [service]
                    .filter(service => runtimeStore.isServiceVoucherAvailable(service))
                    .map(item => ({selected: this.allServicesPreselected, ...item}))
                this.documents = []
                this.customDocuments = []
                this.invoices = []
            }
            this.directSales = clientType === 'DIRECT_SALES'
            let clientName
            if (this.directSales) {
                clientName = `${clientPerson.prefix} ${clientPerson.lastName}`
            } else {
                const firstService = service || this.getSortedServices(services)[0]
                const tourLead = firstService.travelers.find(({isTourLead}) => isTourLead)
                clientName = `${tourLead.prefix} ${tourLead.name[0].lastName}`
            }
            const paidAmount = orderPrices
                .find(({type}) => type === 'CLIENT')
                .orderPrices.reduce((paidAmount, orderPrice) => {
                    const printableAmount = this.$filters.priceFormat(
                        orderPrice.amount - orderPrice.dueToPay,
                        orderPrice.currency
                    )
                    paidAmount.push(printableAmount)
                    return paidAmount
                }, [])
            this.templateData = {
                orderId,
                clientName,
                customerPaymentDate: customerPaymentDate ? customerPaymentDate.split(' ')[0] : '',
                paidAmount: paidAmount.join(' '),
                userName: `${authStore.person.firstName || ''} ${authStore.person.lastName || ''}`.trim(),
            }
        }

        initCustomDocuments(documentTemplates) {
            this.customDocuments = documentTemplates.map(({documentType, id}) => ({
                selected: false,
                name: documentType,
                id,
                filename: customDocumentFileName(this.order, {documentType}),
            }))
        }

        async send() {
            if (!this.valid || this.loading) return
            try {
                this.loading = true
                this.message.attachments = await Promise.all(
                    this.attachments.map(async file => {
                        const path = await blobToBase64(file)
                        return {
                            path,
                            filename: file.name,
                        }
                    })
                )
                const docs = {
                    vouchers: this.services
                        .filter(({selected}) => selected)
                        .map(service => ({
                            processId: service.processId,
                            filename: this.getVoucherFileName(service, this.orderId),
                        })),
                    invoices: this.invoices
                        .filter(({selected}) => selected)
                        .map(({invoiceId, invoiceName}) => ({invoiceId, filename: `${invoiceName}.pdf`})),
                    documents: this.documents.filter(({selected}) => selected).map(document => document.id),
                    customDocuments: this.customDocuments.filter(({selected}) => selected),
                }
                const msg = {...this.message}
                if (this.templateId) {
                    msg.html = this.emailHtml
                } else {
                    msg.html = wrapHtmlMail(this.emailHtml)
                }
                await this.$api.mail.post({
                    message: msg,
                    docs,
                    orderId: this.orderId,
                    //TODO Need refactoring with DownloadCustomDocumentButton
                    orderType: authStore.orderType,
                    isAgency: authStore.isAgency,
                    checkboxServicesSelected: this.checkboxServicesSelected,
                })
                this.$toast.success(this.$t('success_message.send_email'))
                this.modal = false
                this.message = message()
                this.attachments = []
                this.attachmentsPrevious = []
                this.invoices = []
                this.documents = []
                this.services = []
                this.service = null
                this.orderId = null
                this.emailHtml = ''
            } catch (e) {
                console.error(e)
                this.$toast.error(this.$t('error_message.send_email_error'))
            } finally {
                this.loading = false
            }
        }

        getVoucherFileName(service, orderId) {
            return (
                this.$t(`serviceType.${service.serviceType}`) +
                `-Voucher-${orderId}-` +
                service.serviceName.replace(/\s/g, '_') +
                '.pdf'
            )
        }

        allSelected(list) {
            return list.every(({selected}) => selected)
        }

        toggleAllSelected(list) {
            const selected = !this.allSelected(list)
            list.forEach(item => {
                item.selected = selected
            })
        }

        get isAgency() {
            return authStore.isAgency
        }

        get templateItems() {
            return this.templates.filter(({recipientType, notificationType}) => {
                if (this.emailFor?.length) {
                    return (
                        (recipientType === 'B2C' && this.emailFor?.includes('travelers')) ||
                        (recipientType === 'SUPPLIER' && this.emailFor?.includes('suppliers')) ||
                        (recipientType === 'COMPANY' && this.emailFor?.includes('clients'))
                    )
                }

                return (
                    notificationType !== 'PAYMENT_RECEIPT' ||
                    (this.directSales && recipientType === 'B2C') ||
                    (!this.directSales && recipientType === 'COMPANY')
                )
            })
        }

        // get emailHtml() {
        //     if (!this.selectedTemplate?.htmlTemplate) return null
        //     const htmlTemplate = this.emailHtml || this.selectedTemplate.htmlTemplate
        //     const template = Handlebars.compile(htmlTemplate)
        //     return template(this.emailContext)
        // }

        // get emailJson() {
        //     if (!this.selectedTemplate) return null
        //     return this.selectedTemplate.jsonTemplate
        // }

        get emailSubject() {
            if (!this.selectedTemplate.subjectTemplate) return null
            const subject = Handlebars.compile(this.selectedTemplate.subjectTemplate)
            return subject(this.emailContext)
        }

        get emailContext() {
            let clientName
            if (this.directSales) {
                clientName = `${this.order.clientPerson.prefix || ''} ${this.order.clientPerson.lastName || ''}`
            } else {
                const firstService = this.service || this.getSortedServices(this.services)[0]
                const tourLead = firstService.travelers.find(({isTourLead}) => isTourLead)
                clientName = `${tourLead.prefix} ${tourLead.name[0].lastName || ''}`
            }

            return {
                userName: `${authStore.person.firstName || ''} ${authStore.person.lastName || ''}`.trim(),
                clientName,
                orderNumber: this.orderId,
            }
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isTO2() {
            return authStore.isTO2
        }

        get allServicesPreselected() {
            return false
        }

        get isShowVoucherBtn() {
            return true
        }
    }

    export default SendEmailModal
</script>
